// src/Components/GoogleMap/MarkerManager.js

import { useCallback, useRef, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { doc, deleteField, writeBatch, getDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth, storage } from '../../Firebase/firebase';
import {
  MARKER_NEW_SVG_STRING,
  HOME_MARKER_SVG_STRING,
} from '../../assets/icons/CustomIcons';

import { useIcons } from '../../Contexts/IconContext';

// ============================================================================
// MAP PERFORMANCE TUNING CONSTANTS
// ============================================================================
const MAX_DISPLAYED_MARKERS = 500;
const SAMPLING_GRID_SIZE = 10;

const MAX_MARKERS_PER_CELL = Math.ceil(
  MAX_DISPLAYED_MARKERS / (SAMPLING_GRID_SIZE * SAMPLING_GRID_SIZE),
);
// ============================================================================

const NEW_ICON_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  MARKER_NEW_SVG_STRING,
)}`;
const HOME_ICON_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  HOME_MARKER_SVG_STRING,
)}`;

const DEFAULT_ICON = NEW_ICON_SVG;
const BASE_MARKER_SIZE = 38.4;

const sanitizeForFirestore = (data) => {
  const clean = { ...data };
  Object.keys(clean).forEach((key) => {
    const val = clean[key];
    if (val === undefined) {
      clean[key] = null;
    } else if (val instanceof File || val instanceof FileList) {
      delete clean[key];
    }
  });
  return clean;
};

// Cleanly normalizes shifted screen longitudes back to [-180, 180] before saving
const normalizeLng = (lng) => {
  let l = parseFloat(lng);
  if (isNaN(l)) return 0;
  return ((((l + 180) % 360) + 360) % 360) - 180;
};

// Deterministic hash to permanently assign marker copies to a specific continent layout
const getDeterministicHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0;
  }
  return hash;
};

const ROGUE_FIELDS = [
  'groupHasMarker',
  'groupHasPolygon',
  'groupHasPolyline',
  'initialStyleIcon',
  'icon',
  'iconID',
  'isSaved',
  'type',
  'id',
  'isDrawing',
  'isDrawingLine',
  'isPlacingMarker',
  'path',
  'paths',
  'position',
  'color',
  'weight',
  'radius',
  'geometry',
  'markerGroupID',
  'isSilentAutoSave',
  '_screenX',
  '_screenY',
  '_shiftIndex',
  '_shiftedPos',
];

export function useMarkerManager(
  mapInstance,
  markersData,
  setMarkersData,
  markerSelectionMode,
  setTooltip,
  setSelectedMarkerData,
  isDrawerEditing,
  globalLockRef,
  setIsSaving,
  activeMenu,
  drawingStyles,
  clearOthers,
  setInteractionLock,
  setDrawingStyles,
  hiddenMarkerId,
  selectedMarkerProp,
  isDrawMode,
  interactionLockRef,
  activeTool,
  setMarkerCounts,
) {
  const mapRef = mapInstance;

  if (mapRef.current && !window.__loktrMapInstance) {
    window.__loktrMapInstance = mapRef.current;
  }

  const { iconsById, defaultIcon, defaultIconID, iconScale } = useIcons();

  const defaultIconRef = useRef(DEFAULT_ICON);
  const defaultIconIDRef = useRef(null);
  const iconsByIdRef = useRef({});
  const drawingStylesRef = useRef(drawingStyles);

  const markersDataRef = useRef(markersData);
  markersDataRef.current = markersData;

  const originalStateRef = useRef({});

  const markerSelectionModeRef = useRef(markerSelectionMode);
  markerSelectionModeRef.current = markerSelectionMode;

  const activeMenuRef = useRef(activeMenu);
  activeMenuRef.current = activeMenu;

  const markerRefs = useRef({});
  const activeMarkerIdRef = useRef(null);
  const rotationAnimationIdRef = useRef(null);
  const isEditingMarkerRef = useRef(false);
  const isPlacingMarkerRef = useRef(false);
  const iconScaleRef = useRef(iconScale);

  const batchAnimationIdRef = useRef(null);

  const [isRenderingMarkers, setIsRenderingMarkers] = useState(false);

  const isPermittedUserRef = useRef(false);
  const restrictedSettingsRef = useRef(null);

  const setMarkerCountsRef = useRef(setMarkerCounts);
  useEffect(() => {
    setMarkerCountsRef.current = setMarkerCounts;
  }, [setMarkerCounts]);

  const setSelectedMarkerDataRef = useRef(setSelectedMarkerData);
  useEffect(() => {
    setSelectedMarkerDataRef.current = setSelectedMarkerData;
  }, [setSelectedMarkerData]);

  const setTooltipRef = useRef(setTooltip);
  useEffect(() => {
    setTooltipRef.current = setTooltip;
  }, [setTooltip]);

  const clearOthersRef = useRef(clearOthers);
  useEffect(() => {
    clearOthersRef.current = clearOthers;
  }, [clearOthers]);

  const setInteractionLockRef = useRef(setInteractionLock);
  useEffect(() => {
    setInteractionLockRef.current = setInteractionLock;
  }, [setInteractionLock]);

  const setIsSavingRef = useRef(setIsSaving);
  useEffect(() => {
    setIsSavingRef.current = setIsSaving;
  }, [setIsSaving]);

  const selectedMarkerPropRef = useRef(selectedMarkerProp);
  useEffect(() => {
    selectedMarkerPropRef.current = selectedMarkerProp;
  }, [selectedMarkerProp]);

  const hiddenMarkerIdRef = useRef(hiddenMarkerId);
  useEffect(() => {
    hiddenMarkerIdRef.current = hiddenMarkerId;
  }, [hiddenMarkerId]);

  const activeToolRef = useRef(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  const tempMarkerRef = useRef(null);

  const [isPlacingMarker, setIsPlacingMarker] = useState(false);
  const [isEditingMarker, setIsEditingMarker] = useState(false);
  const [activeMarkerId, setActiveMarkerId] = useState(null);

  const isClickableRef = useRef(true);

  useEffect(() => {
    if (!document.getElementById('loktr-global-marker-styles')) {
      const style = document.createElement('style');
      style.id = 'loktr-global-marker-styles';
      style.innerHTML = `
        .loktr-is-dragging .custom-marker-container {
           pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchSettings = async () => {
      try {
        const restRef = doc(db, 'settings', 'RestrictedMarkerSettings');
        const restSnap = await getDoc(restRef);
        if (isMounted && restSnap.exists()) {
          restrictedSettingsRef.current = restSnap.data();
        }
      } catch (e) {
        console.error('Error fetching restricted settings', e);
      }
    };
    fetchSettings();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      if (user && user.email) {
        try {
          const emailKey = user.email.toLowerCase().trim();
          const permRef = doc(db, 'permittedUsers', emailKey);
          const permSnap = await getDoc(permRef);
          if (isMounted) {
            isPermittedUserRef.current = permSnap.exists();
          }
        } catch (e) {
          console.error('Error fetching permission', e);
        }
      } else {
        if (isMounted) {
          isPermittedUserRef.current = false;
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const handleDragStart = () => {
      window.__loktrMapDragging = true;
      mapRef.current.getDiv().classList.add('loktr-is-dragging');
      if (setTooltipRef.current) setTooltipRef.current(null);
    };

    const handleDragEnd = () => {
      window.__loktrMapDragging = false;
      mapRef.current.getDiv().classList.remove('loktr-is-dragging');
    };

    const dragStartListener = window.google.maps.event.addListener(
      mapRef.current,
      'dragstart',
      handleDragStart,
    );
    const dragEndListener = window.google.maps.event.addListener(
      mapRef.current,
      'dragend',
      handleDragEnd,
    );

    return () => {
      window.google.maps.event.removeListener(dragStartListener);
      window.google.maps.event.removeListener(dragEndListener);
    };
  }, [mapRef]);

  useEffect(() => {
    return () => {
      if (batchAnimationIdRef.current) {
        clearTimeout(batchAnimationIdRef.current);
      }
    };
  }, []);

  const setMarkersClickable = useCallback((clickable) => {
    isClickableRef.current = clickable;

    Object.keys(markerRefs.current).forEach((id) => {
      const marker = markerRefs.current[id];
      if (marker) {
        const isActive = id === activeMarkerIdRef.current;

        const shouldBeAlive = clickable || (isPlacingMarkerRef.current && isActive);
        const shouldBeDead = !shouldBeAlive;

        const finalZIndex = shouldBeAlive ? (isActive ? 2000 : 1500) : -1;
        if (marker.zIndex !== finalZIndex) marker.zIndex = finalZIndex;

        try {
          if (marker.gmpClickable !== shouldBeAlive) {
            marker.gmpClickable = shouldBeAlive;
          }
        } catch (e) {}

        const finalPointerEvents = shouldBeAlive ? 'auto' : 'none';
        const finalCursor = shouldBeDead ? 'crosshair' : isActive ? 'grab' : 'pointer';

        if (marker.content) {
          let needsStyleUpdate = false;

          if (marker._appliedPointerEvents !== finalPointerEvents) {
            marker._appliedPointerEvents = finalPointerEvents;
            marker.content.style.setProperty(
              'pointer-events',
              finalPointerEvents,
              'important',
            );
            needsStyleUpdate = true;
          }

          if (marker._appliedCursor !== finalCursor) {
            marker._appliedCursor = finalCursor;
            marker.content.style.setProperty('cursor', finalCursor, 'important');
            needsStyleUpdate = true;
          }

          if (needsStyleUpdate) {
            const wrapper = marker.content.closest('gmp-advanced-marker');
            if (wrapper) {
              wrapper.style.setProperty(
                'pointer-events',
                finalPointerEvents,
                'important',
              );
              wrapper.style.setProperty('cursor', finalCursor, 'important');
            }

            const allChildren = marker.content.querySelectorAll('*');
            for (let k = 0; k < allChildren.length; k++) {
              allChildren[k].style.setProperty(
                'pointer-events',
                finalPointerEvents,
                'important',
              );
              allChildren[k].style.setProperty('cursor', finalCursor, 'important');
            }
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    setMarkersClickable(isClickableRef.current);
  }, [isDrawMode, setMarkersClickable]);

  useEffect(() => {
    if (defaultIcon) defaultIconRef.current = defaultIcon;
    if (defaultIconID) defaultIconIDRef.current = defaultIconID;
    if (iconsById) iconsByIdRef.current = iconsById;
  }, [defaultIcon, defaultIconID, iconsById]);

  useEffect(() => {
    drawingStylesRef.current = drawingStyles;
  }, [drawingStyles]);

  useEffect(() => {
    window.__markerArray = markersData;
  }, [markersData]);

  useEffect(() => {
    isEditingMarkerRef.current = isEditingMarker;
  }, [isEditingMarker]);
  useEffect(() => {
    isPlacingMarkerRef.current = isPlacingMarker;
  }, [isPlacingMarker]);

  const stopRotation = useCallback(() => {
    if (rotationAnimationIdRef.current) {
      cancelAnimationFrame(rotationAnimationIdRef.current);
      rotationAnimationIdRef.current = null;
    }
    Object.values(markerRefs.current).forEach((marker) => {
      if (marker?.content) {
        const wrapper = marker.content.querySelector('.marker-spin-wrapper');
        if (wrapper) wrapper.style.transform = '';
      }
    });
  }, []);

  const startRotation = useCallback(
    (marker) => {
      if (!marker?.content) return;
      if (!isPlacingMarkerRef.current && !isEditingMarkerRef.current) {
        stopRotation();
        return;
      }

      if (activeToolRef.current !== 'MARKER') {
        stopRotation();
        return;
      }

      const spinEl = marker.content.querySelector('.marker-spin-wrapper');
      if (!spinEl) return;

      stopRotation();

      const DURATION_MS = 1000;
      const period = 500;
      let startTime = 0;
      const animate = (timestamp) => {
        if (
          !marker.map ||
          !marker.content ||
          marker.metadata?.id !== activeMarkerIdRef.current ||
          (!isPlacingMarkerRef.current && !isEditingMarkerRef.current) ||
          activeToolRef.current !== 'MARKER'
        ) {
          stopRotation();
          return;
        }
        if (startTime === 0) startTime = timestamp;
        const elapsed = timestamp - startTime;
        if (elapsed >= DURATION_MS) {
          stopRotation();
          return;
        }
        const angle = (elapsed / period) * 360;
        const currentSpinEl = marker.content.querySelector('.marker-spin-wrapper');
        if (currentSpinEl) currentSpinEl.style.transform = `rotateY(${angle}deg)`;
        rotationAnimationIdRef.current = requestAnimationFrame(animate);
      };
      rotationAnimationIdRef.current = requestAnimationFrame(animate);
    },
    [stopRotation],
  );

  const startRotationRef = useRef(startRotation);
  useEffect(() => {
    startRotationRef.current = startRotation;
  }, [startRotation]);

  const stopRotationRef = useRef(stopRotation);
  useEffect(() => {
    stopRotationRef.current = stopRotation;
  }, [stopRotation]);

  useEffect(() => {
    if (activeMarkerId && markerRefs.current[activeMarkerId]) {
      startRotation(markerRefs.current[activeMarkerId]);
    } else {
      stopRotation();
    }
    return () => stopRotation();
  }, [activeMarkerId, startRotation, stopRotation]);

  useEffect(() => {
    if (activeTool !== 'MARKER') {
      stopRotation();
    } else if (
      activeMarkerIdRef.current &&
      markerRefs.current[activeMarkerIdRef.current]
    ) {
      if (isEditingMarkerRef.current || isPlacingMarkerRef.current) {
        startRotation(markerRefs.current[activeMarkerIdRef.current]);
      }
    }
  }, [activeTool, startRotation, stopRotation]);

  // AMENDED: Intercepts isForceSelect to guarantee the backup cache includes media arrays
  const handleMarkerSelect = useCallback(
    (id, data, isForceSelect = false) => {
      if (isPlacingMarkerRef.current) {
        return;
      }

      if (clearOthersRef.current) {
        const canProceed = clearOthersRef.current('MARKER', {
          ...data,
          id,
          type: 'marker',
        });
        if (!canProceed) return;
      }

      // AMENDED: Explicitly cache the fully hydrated marker (including photos)
      if (isForceSelect) {
        originalStateRef.current[id] = JSON.parse(JSON.stringify(data));
      } else if (activeMarkerIdRef.current !== id) {
        const mData = markersDataRef.current.find((m) => m.id === id);
        if (mData) {
          originalStateRef.current[id] = JSON.parse(JSON.stringify(mData));
        }
      }

      if (id === activeMarkerIdRef.current) return;

      stopRotation();
      tempMarkerRef.current = { ...data, id };
      if (markerRefs.current[id]) {
        markerRefs.current[id].metadata = { ...markerRefs.current[id].metadata, ...data };
      }
      activeMarkerIdRef.current = id;
      setActiveMarkerId(id);

      setIsEditingMarker(true);
      isEditingMarkerRef.current = true;

      const marker = markerRefs.current[id];
      if (marker) {
        if (activeMenuRef.current === 'HOME' || activeMenuRef.current === 'VIEW_INFO') {
          assignMarkerContentWithClickRef.current(marker, HOME_ICON_SVG);
        } else {
          assignMarkerContentWithClickRef.current(marker, NEW_ICON_SVG);
        }
        marker.content.classList.add('loktr-active-marker');
      }

      if (setSelectedMarkerDataRef.current)
        setSelectedMarkerDataRef.current({ ...data, id, type: 'marker' });
    },
    [stopRotation],
  );

  const createMarkerIcon = useCallback((src) => {
    const currentScale =
      typeof iconScaleRef.current === 'number' ? iconScaleRef.current : 100;
    const targetSizePx = (BASE_MARKER_SIZE * currentScale) / 100;

    const container = document.createElement('div');
    container.className = 'custom-marker-container';

    container.style.width = `${targetSizePx}px`;
    container.style.height = `${targetSizePx}px`;
    container.style.cursor = 'pointer';
    container.style.perspective = '1000px';

    const spinWrap = document.createElement('div');
    spinWrap.className = 'marker-spin-wrapper';
    spinWrap.style.width = '100%';
    spinWrap.style.height = '100%';
    spinWrap.style.display = 'flex';
    spinWrap.style.justifyContent = 'center';
    spinWrap.style.alignItems = 'center';
    spinWrap.style.transformStyle = 'preserve-3d';

    const img = document.createElement('img');
    let finalSrc = src || DEFAULT_ICON;
    if (typeof finalSrc !== 'string') finalSrc = DEFAULT_ICON;
    img.src = finalSrc;
    img.className = 'marker-icon';
    img.draggable = false;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'contain';
    img.onerror = () => {
      if (img.src !== DEFAULT_ICON) img.src = DEFAULT_ICON;
    };

    spinWrap.appendChild(img);
    container.appendChild(spinWrap);
    return container;
  }, []);

  const shouldAllowClick = useCallback(() => {
    return ['HOME', 'MARKUP_INFO', 'VIEW_INFO', 'UPDATE_INFO', 'MARKUP_MARKERS'].includes(
      activeMenuRef.current,
    );
  }, []);

  const assignMarkerContentWithClick = useCallback(
    (marker, iconName) => {
      const dom = createMarkerIcon(iconName);

      dom.addEventListener('mouseenter', (e) => {
        if (isPlacingMarkerRef.current || window.__loktrMapDragging) return;
        const currentData = marker.metadata || {};
        if (setTooltipRef.current) {
          setTooltipRef.current({
            source: 'MARKER',
            x: e.clientX,
            y: e.clientY,
            content: { image: currentData.hoverImage, body: currentData.hoverContent },
          });
        }
      });

      dom.addEventListener('mouseleave', () => {
        if (window.__loktrMapDragging) return;
        if (setTooltipRef.current) setTooltipRef.current(null);
      });

      dom.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        window.__shapeClickLock = true;
        setTimeout(() => {
          window.__shapeClickLock = false;
        }, 300);

        if (isPlacingMarkerRef.current || window.__loktrMapDragging) return;
        if (!shouldAllowClick()) return;

        const currentId = activeMarkerIdRef.current;
        const newId = marker.metadata?.id;
        if (currentId === newId && activeToolRef.current === 'MARKER') return;

        if (marker.metadata) {
          handleMarkerSelect(marker.metadata.id, marker.metadata);
        }
      });

      if (window.google && window.google.maps) {
        marker.addListener('gmp-click', (e) => {
          if (e.domEvent) {
            e.domEvent.stopPropagation();
            e.domEvent.preventDefault();
          }
        });
      }

      try {
        marker.tabIndex = -1;
      } catch (e) {}

      marker._currentIconName = iconName;
      marker.content = dom;
    },
    [createMarkerIcon, handleMarkerSelect, shouldAllowClick],
  );

  const assignMarkerContentWithClickRef = useRef(assignMarkerContentWithClick);
  useEffect(() => {
    assignMarkerContentWithClickRef.current = assignMarkerContentWithClick;
  }, [assignMarkerContentWithClick]);

  const getValidPosition = (m) => {
    if (!m) return null;
    if (typeof m.lat === 'number' && typeof m.lng === 'number')
      return { lat: m.lat, lng: m.lng };
    if (m.position) {
      const lat =
        typeof m.position.lat === 'function' ? m.position.lat() : m.position.lat;
      const lng =
        typeof m.position.lng === 'function' ? m.position.lng() : m.position.lng;
      if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
    }
    return null;
  };

  // ============================================================================
  // 🛑 CRITICAL ALGORITHM: SCREEN PROJECTION GRID - DO NOT MODIFY 🛑
  // Flawlessly distributes markers across infinite repeated continents on
  // ultrawide dual-monitor setups without zooming constraints.
  // ============================================================================
  const loadMarkers = useCallback(
    (google) => {
      if (!mapRef.current || !google || !google.maps || !google.maps.marker) return;

      const mapDiv = mapRef.current.getDiv();
      const projection = mapRef.current.getProjection();
      const zoom = mapRef.current.getZoom();
      const center = mapRef.current.getCenter();

      if (!mapDiv || !projection || zoom === undefined || !center) return;

      const mapWidth = mapDiv.clientWidth;
      const mapHeight = mapDiv.clientHeight;
      if (mapWidth === 0 || mapHeight === 0) return;

      const scale = Math.pow(2, zoom);
      const worldWidth = 256 * scale;

      const centerPoint = projection.fromLatLngToPoint(center);
      const centerPx = centerPoint.x * scale;
      const centerPy = centerPoint.y * scale;

      // Calculate the physical screen boundary in absolute map pixels
      const leftPx = centerPx - mapWidth / 2;
      const rightPx = centerPx + mapWidth / 2;
      const topPx = centerPy - mapHeight / 2;
      const bottomPx = centerPy + mapHeight / 2;

      const cellWidth = mapWidth / SAMPLING_GRID_SIZE;
      const cellHeight = mapHeight / SAMPLING_GRID_SIZE;

      let mergedMarkers = [...markersDataRef.current];

      if (selectedMarkerPropRef.current && selectedMarkerPropRef.current.id) {
        const idx = mergedMarkers.findIndex(
          (m) => m.id === selectedMarkerPropRef.current.id,
        );
        const cleanPos = getValidPosition(selectedMarkerPropRef.current);
        if (idx !== -1) {
          mergedMarkers[idx] = {
            ...mergedMarkers[idx],
            ...selectedMarkerPropRef.current,
            position: cleanPos || mergedMarkers[idx].position,
          };
        } else if (cleanPos) {
          mergedMarkers.push({
            ...selectedMarkerPropRef.current,
            position: cleanPos,
            type: 'marker',
          });
        }
      }

      if (tempMarkerRef.current && tempMarkerRef.current.id) {
        const exists = mergedMarkers.some((m) => m.id === tempMarkerRef.current.id);
        if (!exists) {
          const cleanPos = getValidPosition(tempMarkerRef.current);
          if (cleanPos) {
            mergedMarkers.push({
              ...tempMarkerRef.current,
              position: cleanPos,
              type: 'marker',
            });
          }
        }
      }

      const today = new Date().toISOString().split('T')[0];
      const isPermitted = isPermittedUserRef.current;

      const instances = [];
      let totalInViewCount = 0;

      // STEP 1: Find every valid physical copy of every marker currently visible on your monitor
      mergedMarkers.forEach((m) => {
        const pos = getValidPosition(m);
        if (!pos) return;

        const pt = projection.fromLatLngToPoint(
          new window.google.maps.LatLng(pos.lat, pos.lng),
        );
        const baseY = pt.y * scale;

        // Skip completely if off-screen vertically
        if (baseY < topPx - 50 || baseY > bottomPx + 50) {
          if (m.id !== activeMarkerIdRef.current && !m.isNew) return;
        }

        if (!isPermitted) {
          let vis = m.visibility;
          if (!vis) {
            if (m.publicFromDate && m.publicFromDate.trim() !== '')
              vis = `Public From ${m.publicFromDate}`;
            else if (m.isPrivate) vis = 'Private';
            else vis = 'Public';
          }

          if (vis === 'Private') return;
          if (vis && vis.startsWith('Public From')) {
            const datePart = vis.replace('Public From', '').trim();
            if (datePart && datePart > today) return;
          }
        }

        const baseX = pt.x * scale;

        // Find how many horizontal wraps exist on screen for this marker
        let minShift = Math.ceil((leftPx - baseX - 50) / worldWidth);
        let maxShift = Math.floor((rightPx - baseX + 50) / worldWidth);

        // Failsafe: Ensure active markers always render even if dragged off-screen
        if ((m.id === activeMarkerIdRef.current || m.isNew) && minShift > maxShift) {
          const closestShift = Math.round((centerPx - baseX) / worldWidth);
          minShift = closestShift;
          maxShift = closestShift;
        }

        let added = false;
        // Generate a virtual instance for every copy that physically fits on your screen
        for (let shift = minShift; shift <= maxShift; shift++) {
          const worldX = baseX + shift * worldWidth;

          // Pinpoint exact location on the monitor
          const screenX = worldX - leftPx;
          const screenY = baseY - topPx;
          const shiftedLng = pos.lng + shift * 360;

          instances.push({
            ...m,
            _screenX: screenX,
            _screenY: screenY,
            _shiftedPos: { lat: pos.lat, lng: shiftedLng },
            _shiftIndex: shift, // Store the shift ID for deterministic hashing
          });
          added = true;
        }
        if (added) totalInViewCount++;
      });

      // STEP 2: Deterministic Sort to ensure equal distribution across all visible continents
      instances.sort((a, b) => {
        const hashA = getDeterministicHash(String(a.id) + a._shiftIndex);
        const hashB = getDeterministicHash(String(b.id) + b._shiftIndex);
        return hashA - hashB;
      });

      const gridTracker = {};
      const markersToRender = [];
      const usedMarkerIds = new Set(); // Guarantees a marker is never physically drawn twice

      // Priority pass for the active/dragged marker
      instances.forEach((inst) => {
        if (inst.id === activeMarkerIdRef.current || inst.isNew) {
          if (!usedMarkerIds.has(inst.id)) {
            markersToRender.push(inst);
            usedMarkerIds.add(inst.id);
          }
        }
      });

      // Pass 1: Primary Distribution
      for (let i = 0; i < instances.length; i++) {
        if (markersToRender.length >= MAX_DISPLAYED_MARKERS) break;

        const inst = instances[i];

        // If marker is already rendered on one continent, skip its ghost copies!
        if (usedMarkerIds.has(inst.id)) continue;

        let col = Math.floor(inst._screenX / cellWidth);
        let row = Math.floor(inst._screenY / cellHeight);

        // Safely clamp bounds
        col = Math.max(0, Math.min(SAMPLING_GRID_SIZE - 1, col));
        row = Math.max(0, Math.min(SAMPLING_GRID_SIZE - 1, row));

        const cellKey = `${row}-${col}`;

        if (!gridTracker[cellKey]) gridTracker[cellKey] = 0;

        if (gridTracker[cellKey] < MAX_MARKERS_PER_CELL) {
          gridTracker[cellKey]++;
          markersToRender.push(inst);
          usedMarkerIds.add(inst.id);
        }
      }

      // Pass 2: Fallback Top-Up
      if (
        markersToRender.length < MAX_DISPLAYED_MARKERS &&
        markersToRender.length < totalInViewCount
      ) {
        for (let i = 0; i < instances.length; i++) {
          if (markersToRender.length >= MAX_DISPLAYED_MARKERS) break;

          const inst = instances[i];
          if (!usedMarkerIds.has(inst.id)) {
            markersToRender.push(inst);
            usedMarkerIds.add(inst.id);
          }
        }
      }
      // ============================================================================

      if (setMarkerCountsRef.current) {
        setMarkerCountsRef.current((prev) => {
          if (
            prev.displayed === markersToRender.length &&
            prev.totalInView === totalInViewCount &&
            prev.total === mergedMarkers.length
          ) {
            return prev;
          }
          return {
            displayed: markersToRender.length,
            totalInView: totalInViewCount,
            total: mergedMarkers.length,
          };
        });
      }

      const currentIdsToRender = usedMarkerIds;
      const isViewingMode = ['HOME', 'VIEW_INFO', 'UPDATE_INFO'].includes(
        activeMenuRef.current,
      );

      Object.keys(markerRefs.current).forEach((id) => {
        const shouldHide = id === hiddenMarkerIdRef.current && !isViewingMode;
        const marker = markerRefs.current[id];
        const isDraft = marker?.metadata?.isNew;

        if (
          (!currentIdsToRender.has(id) && id !== activeMarkerIdRef.current && !isDraft) ||
          shouldHide
        ) {
          if (markerRefs.current[id]) markerRefs.current[id].map = null;
          delete markerRefs.current[id];
        }
      });

      if (batchAnimationIdRef.current) {
        clearTimeout(batchAnimationIdRef.current);
        batchAnimationIdRef.current = null;
      }

      setIsRenderingMarkers(true);
      let currentIndex = 0;
      const CHUNK_SIZE = 50;

      const activeCollisionBehavior = google.maps.CollisionBehavior.REQUIRED;

      const rSettings = restrictedSettingsRef.current;

      const processChunk = () => {
        if (!mapRef.current || !window.google) {
          setIsRenderingMarkers(false);
          return;
        }

        const endIndex = Math.min(currentIndex + CHUNK_SIZE, markersToRender.length);

        try {
          for (let i = currentIndex; i < endIndex; i++) {
            const m = markersToRender[i];

            const renderPos = m._shiftedPos || getValidPosition(m);

            if (!renderPos || (m.id === hiddenMarkerIdRef.current && !isViewingMode))
              continue;

            let displayData = { ...m };
            let overrideIconUrl = null;

            if (!isPermittedUserRef.current) {
              if (displayData.visibility === 'Restricted' && rSettings) {
                overrideIconUrl = rSettings.RestrictedIcon || null;
                displayData.hoverImage = rSettings.RestrictedHoverImage || '';
                displayData.hoverContent = rSettings.RestrictedHoverText || '';
              }
            }

            let marker = markerRefs.current[m.id];
            const isActive = m.id === activeMarkerIdRef.current;

            let isDraggable =
              isActive &&
              activeToolRef.current === 'MARKER' &&
              (markerSelectionModeRef.current ||
                activeMenuRef.current === 'MARKUP_MARKERS');
            if (activeMenuRef.current === 'HOME' || activeMenuRef.current === 'VIEW_INFO')
              isDraggable = false;

            let baseIconUrl = DEFAULT_ICON;
            if (overrideIconUrl) {
              baseIconUrl = overrideIconUrl;
            } else if (m.iconID && iconsByIdRef.current[m.iconID]) {
              baseIconUrl = iconsByIdRef.current[m.iconID].icon;
            } else if (m.icon && typeof m.icon === 'string') {
              baseIconUrl = m.icon;
            }

            let targetIconName = baseIconUrl;
            if (isActive) {
              const isMarkerNew =
                m.isNew ||
                marker?.metadata?.isNew ||
                (selectedMarkerPropRef.current?.id === m.id &&
                  selectedMarkerPropRef.current?.isNew);
              if (activeMenuRef.current === 'MARKUP_MARKERS')
                targetIconName = NEW_ICON_SVG;
              else if (
                activeMenuRef.current === 'MARKUP_INFO' ||
                activeMenuRef.current === 'UPDATE_INFO'
              )
                targetIconName = isMarkerNew ? baseIconUrl : NEW_ICON_SVG;
              else if (
                activeMenuRef.current === 'HOME' ||
                activeMenuRef.current === 'VIEW_INFO'
              )
                targetIconName = isMarkerNew ? baseIconUrl : HOME_ICON_SVG;
            }

            const attachDragListeners = (markerInstance) => {
              markerInstance.addListener('dragstart', () => {
                if (setInteractionLockRef.current) setInteractionLockRef.current(true);
                if (setTooltipRef.current) setTooltipRef.current(null);
                window.__vertexInteractionLock = true;
              });

              markerInstance.addListener('dragend', (e) => {
                const p = markerInstance.position;
                const rawLat = typeof p.lat === 'function' ? p.lat() : p.lat;
                const rawLng = typeof p.lng === 'function' ? p.lng() : p.lng;

                const newPos = { lat: rawLat, lng: normalizeLng(rawLng) };

                if (setSelectedMarkerDataRef.current) {
                  setSelectedMarkerDataRef.current((prev) => ({
                    ...prev,
                    position: newPos,
                  }));
                }

                setMarkersData((prev) =>
                  prev.map((item) =>
                    item.id === m.id ? { ...item, position: newPos } : item,
                  ),
                );

                setTimeout(() => {
                  if (setInteractionLockRef.current) setInteractionLockRef.current(false);
                  window.__vertexInteractionLock = false;
                }, 500);
              });
            };

            if (!marker) {
              marker = new google.maps.marker.AdvancedMarkerElement({
                map: mapRef.current,
                position: renderPos,
                gmpDraggable: isDraggable,
                zIndex: 1001,
                collisionBehavior: activeCollisionBehavior,
              });
              marker.metadata = displayData;
              marker._appliedPointerEvents = null;
              marker._appliedCursor = null;

              if (assignMarkerContentWithClickRef.current)
                assignMarkerContentWithClickRef.current(marker, targetIconName);
              markerRefs.current[m.id] = marker;

              if (isActive) marker.content.classList.add('loktr-active-marker');
              else marker.content.classList.remove('loktr-active-marker');

              if (isDraggable) {
                attachDragListeners(marker);
              }

              if (
                isActive &&
                (isPlacingMarkerRef.current || isEditingMarkerRef.current)
              ) {
                if (startRotationRef.current)
                  requestAnimationFrame(() => startRotationRef.current(marker));
              }
            } else {
              marker.metadata = displayData;

              if (!marker.map) marker.map = mapRef.current;

              if (marker.content) {
                if (isActive) marker.content.classList.add('loktr-active-marker');
                else marker.content.classList.remove('loktr-active-marker');
              }

              if (!isActive || !interactionLockRef.current) {
                const curLat =
                  typeof marker.position?.lat === 'function'
                    ? marker.position.lat()
                    : marker.position?.lat;
                const curLng =
                  typeof marker.position?.lng === 'function'
                    ? marker.position.lng()
                    : marker.position?.lng;

                const posLat = Number(renderPos.lat).toFixed(6);
                const posLng = Number(renderPos.lng).toFixed(6);
                const cLat = Number(curLat).toFixed(6);
                const cLng = Number(curLng).toFixed(6);

                if (cLat !== posLat || cLng !== posLng) {
                  marker.position = renderPos;
                }
              }

              if (marker.gmpDraggable !== isDraggable) {
                marker.gmpDraggable = isDraggable;
                if (isDraggable) attachDragListeners(marker);
              }

              if (marker.collisionBehavior !== activeCollisionBehavior) {
                marker.collisionBehavior = activeCollisionBehavior;
              }

              if (marker._currentIconName !== targetIconName) {
                if (assignMarkerContentWithClickRef.current)
                  assignMarkerContentWithClickRef.current(marker, targetIconName);

                if (marker.content) {
                  if (isActive) marker.content.classList.add('loktr-active-marker');
                  else marker.content.classList.remove('loktr-active-marker');
                }

                if (
                  isActive &&
                  (isPlacingMarkerRef.current || isEditingMarkerRef.current)
                ) {
                  if (startRotationRef.current)
                    requestAnimationFrame(() => startRotationRef.current(marker));
                }
              }
            }

            if (marker) {
              const isMapClickable = isClickableRef.current;
              const shouldBeAlive =
                isMapClickable || (isPlacingMarkerRef.current && isActive);
              const shouldBeDead = !shouldBeAlive;

              const finalZIndex = shouldBeAlive ? (isActive ? 2000 : 1500) : -1;
              if (marker.zIndex !== finalZIndex) marker.zIndex = finalZIndex;

              if (marker.gmpClickable !== shouldBeAlive) {
                try {
                  marker.gmpClickable = shouldBeAlive;
                } catch (e) {}
              }

              const finalPointerEvents = shouldBeAlive ? 'auto' : 'none';
              const finalCursor = shouldBeDead
                ? 'crosshair'
                : isActive
                  ? isDraggable
                    ? 'grab'
                    : 'pointer'
                  : 'pointer';

              if (marker.content) {
                let needsStyleUpdate = false;

                if (marker._appliedPointerEvents !== finalPointerEvents) {
                  marker._appliedPointerEvents = finalPointerEvents;
                  marker.content.style.setProperty(
                    'pointer-events',
                    finalPointerEvents,
                    'important',
                  );
                  needsStyleUpdate = true;
                }

                if (marker._appliedCursor !== finalCursor) {
                  marker._appliedCursor = finalCursor;
                  marker.content.style.setProperty('cursor', finalCursor, 'important');
                  needsStyleUpdate = true;
                }

                if (needsStyleUpdate) {
                  const wrapper = marker.content.closest('gmp-advanced-marker');
                  if (wrapper) {
                    wrapper.style.setProperty(
                      'pointer-events',
                      finalPointerEvents,
                      'important',
                    );
                    wrapper.style.setProperty('cursor', finalCursor, 'important');
                  }

                  const allChildren = marker.content.querySelectorAll('*');
                  for (let k = 0; k < allChildren.length; k++) {
                    allChildren[k].style.setProperty(
                      'pointer-events',
                      finalPointerEvents,
                      'important',
                    );
                    allChildren[k].style.setProperty('cursor', finalCursor, 'important');
                  }
                }
              }
            }
          }
          currentIndex = endIndex;
        } catch (error) {
          console.error('Marker processing error:', error);
          currentIndex = markersToRender.length;
        } finally {
          if (currentIndex < markersToRender.length) {
            batchAnimationIdRef.current = setTimeout(processChunk, 15);
          } else {
            batchAnimationIdRef.current = null;
            setIsRenderingMarkers(false);
          }
        }
      };

      batchAnimationIdRef.current = setTimeout(processChunk, 15);
    },
    [mapRef, interactionLockRef, setMarkersData],
  );

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const listener = window.google.maps.event.addListener(mapRef.current, 'idle', () => {
      loadMarkers(window.google);
    });

    return () => window.google.maps.event.removeListener(listener);
  }, [mapRef, loadMarkers]);

  useEffect(() => {
    if (mapRef.current && window.google) {
      loadMarkers(window.google);
    }
  }, [markersData, loadMarkers, mapRef]);

  useEffect(() => {
    if (iconScaleRef.current !== iconScale) {
      iconScaleRef.current = iconScale;
      Object.keys(markerRefs.current).forEach((id) => {
        const marker = markerRefs.current[id];
        if (marker) {
          assignMarkerContentWithClick(marker, marker._currentIconName);
        }
      });
    }
  }, [iconScale, assignMarkerContentWithClick]);

  // AMENDED: Integrates liveData into the revert payload to protect media arrays
  const deselectViewMarker = useCallback(
    (overrideData = null, shouldClearSelection = true) => {
      stopRotation();
      tempMarkerRef.current = null;
      const idToDeselect = activeMarkerId || activeMarkerIdRef.current;
      activeMarkerIdRef.current = null;
      setActiveMarkerId(null);
      setIsEditingMarker(false);

      if (idToDeselect) {
        const marker = markerRefs.current[idToDeselect];

        const originalData = originalStateRef.current[idToDeselect];
        const liveData =
          markersDataRef.current.find((m) => m.id === idToDeselect) ||
          marker?.metadata ||
          {};

        const revertedData = originalData
          ? {
              ...originalData,
              photos: liveData.photos || originalData.photos,
              documents: liveData.documents || originalData.documents,
              hoverImage: liveData.hoverImage || originalData.hoverImage,
            }
          : liveData;

        const data = overrideData || revertedData;

        if (marker && data) {
          let iconToRestore =
            (data.iconID && iconsByIdRef.current[data.iconID]?.icon) ||
            data.icon ||
            DEFAULT_ICON;
          assignMarkerContentWithClick(marker, iconToRestore);
          marker.gmpDraggable = false;
          marker.metadata = data;
          marker.zIndex = 1;

          if (data.position) {
            const rawLat =
              typeof data.position.lat === 'function'
                ? data.position.lat()
                : data.position.lat;
            const rawLng =
              typeof data.position.lng === 'function'
                ? data.position.lng()
                : data.position.lng;
            const centerLng = mapRef.current.getCenter().lng();
            const shifts = Math.round((centerLng - rawLng) / 360);
            marker.position = { lat: rawLat, lng: rawLng + shifts * 360 };
          }

          if (marker.content) marker.content.classList.remove('loktr-active-marker');

          try {
            marker.gmpClickable = true;
          } catch (e) {}

          if (marker.content) {
            marker.content.style.setProperty('pointer-events', 'auto', 'important');
            marker.content.style.setProperty('cursor', 'pointer', 'important');
            marker._appliedPointerEvents = 'auto';
            marker._appliedCursor = 'pointer';

            const wrapper = marker.content.closest('gmp-advanced-marker');
            if (wrapper) {
              wrapper.style.setProperty('pointer-events', 'auto', 'important');
              wrapper.style.setProperty('cursor', 'pointer', 'important');
            }

            const allChildren = marker.content.querySelectorAll('*');
            for (let k = 0; k < allChildren.length; k++) {
              allChildren[k].style.setProperty('pointer-events', 'auto', 'important');
              allChildren[k].style.setProperty('cursor', 'pointer', 'important');
            }
          }

          setMarkersData((prev) => {
            const next = prev.map((m) => (m.id === idToDeselect ? data : m));
            markersDataRef.current = next;
            return next;
          });
        }
        if (originalStateRef.current[idToDeselect]) {
          delete originalStateRef.current[idToDeselect];
        }
      }
      if (shouldClearSelection && setSelectedMarkerDataRef.current)
        setSelectedMarkerDataRef.current(null);
    },
    [stopRotation, assignMarkerContentWithClick, activeMarkerId, setMarkersData, mapRef],
  );

  const startNewMarker = useCallback(
    (preserve = false) => {
      deselectViewMarker(null, !preserve);
      setIsPlacingMarker(true);
      isPlacingMarkerRef.current = true;
      setIsEditingMarker(false);
      isEditingMarkerRef.current = false;
      if (mapRef.current) mapRef.current.setOptions({ draggableCursor: 'crosshair' });
    },
    [mapRef, deselectViewMarker],
  );

  const handleMapClickForMarker = useCallback(
    (google, event) => {
      if (!google || !google.maps || !google.maps.marker || !isPlacingMarkerRef.current)
        return null;
      const newId = uuidv4();

      const activeCollisionBehavior = google.maps.CollisionBehavior.REQUIRED;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current,
        position: event.latLng,
        gmpDraggable: true,
        zIndex: 1001,
        collisionBehavior: activeCollisionBehavior,
      });

      const styles = drawingStylesRef.current || {};
      let selectedIconID = styles.iconID || defaultIconIDRef.current;
      let selectedIcon = styles.markerIcon || defaultIconRef.current;

      const existingGroupId =
        selectedMarkerPropRef.current?.markerGroupID ||
        selectedMarkerPropRef.current?.id ||
        undefined;

      const newMarkerData = {
        id: newId,
        position: event.latLng.toJSON(),
        iconID: selectedIconID,
        icon: selectedIcon,
        type: 'marker',
        isNew: true,
        ...(existingGroupId ? { markerGroupID: existingGroupId } : {}),
      };

      tempMarkerRef.current = newMarkerData;
      setMarkersData((prev) => {
        const next = [...prev, newMarkerData];
        markersDataRef.current = next;
        return next;
      });

      marker.metadata = newMarkerData;
      marker._appliedPointerEvents = null;
      marker._appliedCursor = null;

      assignMarkerContentWithClick(marker, NEW_ICON_SVG);
      markerRefs.current[newId] = marker;
      marker.content.classList.add('loktr-active-marker');

      marker.addListener('dragstart', () => {
        if (setInteractionLockRef.current) setInteractionLockRef.current(true);
        if (setTooltipRef.current) setTooltipRef.current(null);
      });
      marker.addListener('dragend', () => {
        const p = marker.position;
        const rawLat = typeof p.lat === 'function' ? p.lat() : p.lat;
        const rawLng = typeof p.lng === 'function' ? p.lng() : p.lng;
        const newPos = { lat: rawLat, lng: normalizeLng(rawLng) };
        if (setSelectedMarkerDataRef.current)
          setSelectedMarkerDataRef.current((prev) => ({
            ...prev,
            position: newPos,
          }));
        setMarkersData((prev) => {
          const next = prev.map((item) =>
            item.id === newId ? { ...item, position: newPos } : item,
          );
          markersDataRef.current = next;
          return next;
        });
        setTimeout(() => {
          if (setInteractionLockRef.current) setInteractionLockRef.current(false);
        }, 200);
      });

      activeMarkerIdRef.current = newId;
      setActiveMarkerId(newId);
      setIsEditingMarker(true);
      isEditingMarkerRef.current = true;
      setIsPlacingMarker(false);
      isPlacingMarkerRef.current = false;
      mapRef.current.setOptions({ draggableCursor: 'grab' });
      if (setSelectedMarkerDataRef.current)
        setSelectedMarkerDataRef.current(newMarkerData);
      return newMarkerData;
    },
    [mapRef, assignMarkerContentWithClick, setMarkersData],
  );

  const saveMarker = useCallback(
    async (groupDataPayload) => {
      if (setIsSavingRef.current) setIsSavingRef.current(true);
      const id = groupDataPayload?.id || activeMarkerIdRef.current || uuidv4();

      const marker = markerRefs.current[id];
      const metadata = marker?.metadata || {};
      const memoryMarker = markersDataRef.current.find((m) => m.id === id) || {};

      const cleanGroupData = sanitizeForFirestore({ ...groupDataPayload });
      const groupId = cleanGroupData.markerGroupID || id;

      const finalIconID =
        groupDataPayload.iconID ?? metadata.iconID ?? memoryMarker.iconID ?? null;

      const finalIconString =
        groupDataPayload.icon ?? metadata.icon ?? memoryMarker.icon ?? null;

      let livePos = null;
      if (marker && marker.position) {
        const rawLat =
          typeof marker.position.lat === 'function'
            ? marker.position.lat()
            : marker.position.lat;
        const rawLng =
          typeof marker.position.lng === 'function'
            ? marker.position.lng()
            : marker.position.lng;
        livePos = { lat: rawLat, lng: normalizeLng(rawLng) };
      }

      let currentPos =
        livePos ||
        groupDataPayload.position ||
        metadata.position ||
        memoryMarker.position ||
        null;

      ROGUE_FIELDS.forEach((field) => delete cleanGroupData[field]);

      try {
        const geometryData = {
          position: currentPos,
          iconID: finalIconID,
          markerGroupID: groupId,
        };

        const batch = writeBatch(db);
        batch.set(doc(db, 'markers', id), geometryData, { merge: true });

        if (!groupDataPayload.isSilentAutoSave) {
          ROGUE_FIELDS.forEach((field) => (cleanGroupData[field] = deleteField()));
          batch.set(doc(db, 'markerGroups', groupId), cleanGroupData, { merge: true });
        } else {
          batch.set(doc(db, 'markerGroups', groupId), { markerID: id }, { merge: true });
        }

        await batch.commit();

        const resolvedIconUrl =
          finalIconID && iconsByIdRef.current[finalIconID]
            ? iconsByIdRef.current[finalIconID].icon
            : undefined;

        ROGUE_FIELDS.forEach((field) => delete cleanGroupData[field]);

        const dataWithId = {
          ...cleanGroupData,
          ...geometryData,
          id,
          icon: finalIconString || resolvedIconUrl || DEFAULT_ICON,
          isNew: false,
          isSaved: true,
        };

        if (
          cleanGroupData.publicFromDate &&
          typeof cleanGroupData.publicFromDate === 'object'
        ) {
          delete dataWithId.publicFromDate;
        }

        delete dataWithId.isSilentAutoSave;

        originalStateRef.current[id] = JSON.parse(JSON.stringify(dataWithId));

        if (marker) {
          marker.metadata = {
            ...marker.metadata,
            ...dataWithId,
            isNew: false,
            isSaved: true,
          };
          assignMarkerContentWithClick(marker, dataWithId.icon);

          if (id === activeMarkerIdRef.current && marker.content) {
            marker.content.classList.add('loktr-active-marker');
          }

          if (
            id === activeMarkerIdRef.current &&
            (isPlacingMarkerRef.current || isEditingMarkerRef.current)
          ) {
            requestAnimationFrame(() => startRotation(marker));
          }
        }

        if (!groupDataPayload?.isSilentAutoSave) {
          if (setSelectedMarkerDataRef.current)
            setSelectedMarkerDataRef.current(dataWithId);
        }

        setMarkersData((prev) => {
          const exists = prev.find((m) => m.id === id);
          const next = exists
            ? prev.map((m) => (m.id === id ? { ...m, ...dataWithId } : m))
            : [...prev, dataWithId];
          markersDataRef.current = next;
          return next;
        });
      } catch (err) {
        console.error(err);
      } finally {
        if (setIsSavingRef.current) setIsSavingRef.current(false);
      }
    },
    [setMarkersData, assignMarkerContentWithClick, startRotation],
  );

  const deleteMarker = useCallback(
    async (idToDelete) => {
      if (setTooltipRef.current) setTooltipRef.current(null);
      const id = idToDelete || activeMarkerIdRef.current;
      if (!id) return;
      if (setIsSavingRef.current) setIsSavingRef.current(true);
      try {
        const existingData = markersDataRef.current.find((m) => m.id === id);
        let groupId = existingData?.markerGroupID;

        if (!groupId) {
          const snap = await getDoc(doc(db, 'markers', id));
          if (snap.exists() && snap.data().markerGroupID) {
            groupId = snap.data().markerGroupID;
          }
        }
        groupId = groupId || id;

        const polygons = window.__polygonArray || [];
        const polylines = window.__polylineArray || [];
        const siblingPolygon = polygons.find(
          (p) => String(p.markerGroupID || p.id) === String(groupId),
        );
        const siblingPolyline = polylines.find(
          (l) => String(l.markerGroupID || l.id) === String(groupId),
        );

        if (!siblingPolygon && !siblingPolyline) {
          let hoverImageUrl = existingData?.hoverImage;
          let photosArray = existingData?.photos || [];
          let docsArray = existingData?.documents || [];

          // Fetch from Firestore if data is missing from local state
          if (!hoverImageUrl || photosArray.length === 0 || docsArray.length === 0) {
            const groupSnap = await getDoc(doc(db, 'markerGroups', groupId));
            if (groupSnap.exists()) {
              const groupData = groupSnap.data();
              hoverImageUrl = hoverImageUrl || groupData.hoverImage;
              if (photosArray.length === 0 && groupData.photos)
                photosArray = groupData.photos;
              if (docsArray.length === 0 && groupData.documents)
                docsArray = groupData.documents;
            }
          }

          // 1. Delete Hover Image from storage
          if (hoverImageUrl && hoverImageUrl.startsWith('http')) {
            try {
              const imageRef = ref(storage, hoverImageUrl);
              await deleteObject(imageRef);
            } catch (storageErr) {
              console.warn(
                '[MarkerManager] Failed to delete hover image from storage:',
                storageErr,
              );
            }
          }

          // 2. Delete Gallery Photos from storage
          if (photosArray && photosArray.length > 0) {
            for (const photo of photosArray) {
              if (photo.url && photo.url.startsWith('http')) {
                try {
                  const photoRef = ref(storage, photo.url);
                  await deleteObject(photoRef);
                } catch (err) {
                  console.warn('[MarkerManager] Failed to delete gallery photo:', err);
                }
              }
            }
          }

          // 3. Delete Attached Documents from storage
          if (docsArray && docsArray.length > 0) {
            for (const docItem of docsArray) {
              if (docItem.url && docItem.url.startsWith('http')) {
                try {
                  const docRef = ref(storage, docItem.url);
                  await deleteObject(docRef);
                } catch (err) {
                  console.warn('[MarkerManager] Failed to delete document:', err);
                }
              }
            }
          }
        }

        const batch = writeBatch(db);
        batch.delete(doc(db, 'markers', id));

        if (!siblingPolygon && !siblingPolyline) {
          batch.delete(doc(db, 'markerGroups', groupId));
        } else {
          batch.set(
            doc(db, 'markerGroups', groupId),
            { markerID: deleteField() },
            { merge: true },
          );
        }

        await batch.commit();

        if (markerRefs.current[id]) {
          markerRefs.current[id].map = null;
        }
        delete markerRefs.current[id];

        delete originalStateRef.current[id];

        setMarkersData((prev) => prev.filter((m) => m.id !== id));
        deselectViewMarker();
      } catch (err) {
        console.error(err);
      } finally {
        if (setIsSavingRef.current) setIsSavingRef.current(false);
      }
    },
    [setMarkersData, deselectViewMarker],
  );

  const cancelMarker = useCallback(() => {
    if (setTooltipRef.current) setTooltipRef.current(null);
    const id = activeMarkerIdRef.current;
    const inMemory = id ? markersDataRef.current.find((m) => m.id === id) : null;
    const isUnsavedDraft =
      (id &&
        markerRefs.current[id] &&
        !markerRefs.current[id].metadata?.isSaved &&
        markerRefs.current[id].metadata?.isNew) ||
      (inMemory && inMemory.isNew && !inMemory.isSaved);

    if (id && isUnsavedDraft) {
      if (markerRefs.current[id]) {
        markerRefs.current[id].map = null;
        delete markerRefs.current[id];
      }
      setMarkersData((prev) => {
        const next = prev.filter((m) => m.id !== id);
        markersDataRef.current = next;
        return next;
      });
    }
    deselectViewMarker();
    setIsPlacingMarker(false);
    isPlacingMarkerRef.current = false;
    mapRef.current?.setOptions({ draggableCursor: 'grab' });
  }, [mapRef, deselectViewMarker, setMarkersData]);

  const cancelMarkerEdit = useCallback(
    (shouldClearSelection = true) => {
      if (setTooltipRef.current) setTooltipRef.current(null);
      const id = activeMarkerIdRef.current;
      const inMemory = id ? markersDataRef.current.find((m) => m.id === id) : null;
      const isUnsavedDraft =
        (id &&
          markerRefs.current[id] &&
          !markerRefs.current[id].metadata?.isSaved &&
          markerRefs.current[id].metadata?.isNew) ||
        (inMemory && inMemory.isNew && !inMemory.isSaved);

      if (id && isUnsavedDraft) {
        if (markerRefs.current[id]) {
          markerRefs.current[id].map = null;
          delete markerRefs.current[id];
        }
        setMarkersData((prev) => {
          const next = prev.filter((m) => m.id !== id);
          markersDataRef.current = next;
          return next;
        });
      }
      deselectViewMarker(null, shouldClearSelection);
    },
    [deselectViewMarker, setMarkersData],
  );

  const changeMarkerIcon = useCallback(
    (icon, newIconID) => {
      const id = activeMarkerIdRef.current;
      if (id && markerRefs.current[id]) {
        const marker = markerRefs.current[id];

        if (marker.metadata?.iconID === newIconID && marker.metadata?.icon === icon)
          return;

        marker.metadata = { ...marker.metadata, icon, iconID: newIconID };

        if (activeMenuRef.current === 'MARKUP_INFO')
          assignMarkerContentWithClick(marker, NEW_ICON_SVG);
        else if (
          activeMenuRef.current === 'HOME' ||
          activeMenuRef.current === 'VIEW_INFO'
        )
          assignMarkerContentWithClick(marker, HOME_ICON_SVG);
        else assignMarkerContentWithClick(marker, NEW_ICON_SVG);

        if (marker.content) marker.content.classList.add('loktr-active-marker');

        if (isPlacingMarkerRef.current || isEditingMarkerRef.current) {
          if (startRotationRef.current) {
            requestAnimationFrame(() => startRotationRef.current(marker));
          }
        }

        setMarkersData((prev) => {
          const next = prev.map((m) =>
            m.id === id ? { ...m, icon, iconID: newIconID } : m,
          );
          markersDataRef.current = next;
          return next;
        });

        if (setSelectedMarkerDataRef.current) {
          setSelectedMarkerDataRef.current((prev) =>
            prev && prev.id === id ? { ...prev, icon, iconID: newIconID } : prev,
          );
        }
      }
    },
    [assignMarkerContentWithClick, setMarkersData],
  );

  return {
    isPlacingMarker,
    isEditingMarker,
    isPlacingMarkerRef,
    isEditingMarkerRef,
    activeMarkerId,
    startNewMarker,
    loadMarkers,
    handleMapClickForMarker,
    saveMarker,
    saveMarkerEdits: saveMarker,
    deleteMarker,
    cancelMarker,
    cancelMarkerEdit,
    deselectViewMarker,
    changeMarkerIcon,
    handleMarkerSelect,
    setMarkersClickable,
    isRenderingMarkers,
  };
}
